import { useEffect, useRef } from 'react';
import { publishCounter, publishTimer } from '../utils/metrics';
import { logger } from '../utils/logger';
import type { LoggedInUserInfo } from '../types/auth/auth.types';

interface UseActiveUserTrackingProps {
  user: LoggedInUserInfo | null;
  metricsReady: boolean;
}

/**
 * Custom hook to track active user sessions in real-time
 *
 * This hook:
 * 1. Publishes "SessionStart" when user opens the app
 * 2. Publishes periodic "SessionHeartbeat" while user is active (every 60 seconds)
 * 3. Publishes "SessionEnd" when user closes tab or navigates away
 * 4. Publishes "SessionDuration" timer metric on session end
 *
 * Heartbeats pause when the tab is hidden and resume when it regains focus.
 * Use a 2-minute CloudWatch window to detect "currently active" users,
 * ensuring at least one heartbeat falls within the window.
 *
 * @param user - The authenticated user information
 */
export const useActiveUserTracking = ({ user, metricsReady }: UseActiveUserTrackingProps): void => {
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasStartedSessionRef = useRef(false);
  const sessionStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!user || !metricsReady) {
      hasStartedSessionRef.current = false;
      return;
    }

    // Only start session once per user
    if (hasStartedSessionRef.current) {
      return;
    }

    hasStartedSessionRef.current = true;
    sessionStartTimeRef.current = Date.now();

    const sessionId = generateSessionId();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
    const userDims = { userAlias: user.userAlias, displayName: user.displayName, timezone };

    // Publish SessionStart metric
    publishCounter('SessionStart', 1, undefined, { ...userDims, sessionId });

    logger.info('User session started', { userAlias: user.userAlias, sessionId });

    // Set up periodic heartbeat (every 60 seconds)
    heartbeatIntervalRef.current = setInterval(() => {
      publishCounter('SessionHeartbeat', 1, undefined, userDims);
    }, 60000);

    // Set up SessionEnd tracking on tab close/navigation
    const handleBeforeUnload = (): void => {
      const sessionDuration = sessionStartTimeRef.current ? Date.now() - sessionStartTimeRef.current : 0;

      publishCounter('SessionEnd', 1, undefined, userDims);

      if (sessionDuration > 0) {
        publishTimer('SessionDuration', sessionDuration, undefined, userDims);
      }

      logger.info('User session ended', { userAlias: user.userAlias, sessionDurationMs: sessionDuration });
    };

    // Handle page visibility changes (tab switch, minimize, etc.)
    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
      } else if (!heartbeatIntervalRef.current && user) {
        heartbeatIntervalRef.current = setInterval(() => {
          publishCounter('SessionHeartbeat', 1, undefined, userDims);
        }, 60000);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      const sessionDuration = sessionStartTimeRef.current ? Date.now() - sessionStartTimeRef.current : 0;
      const cleanupDims = {
        userAlias: user?.userAlias || 'unknown',
        displayName: user?.displayName || 'unknown',
      };

      publishCounter('SessionEnd', 1, undefined, cleanupDims);

      if (sessionDuration > 0) {
        publishTimer('SessionDuration', sessionDuration, undefined, cleanupDims);
      }
    };
  }, [user, metricsReady]);
};

/**
 * Generate a unique session ID for tracking individual sessions
 */
const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};
